/**
 * The "Program of Interest" step experiment: PostHog multivariate flag
 * `application-program-step-experiment`, variants `control` and `test`.
 *
 * The persisted `form.step` indices are the same in both variants, so the
 * backend's `step == 6` (submitted) rule and resumes across variants keep
 * working: 1 Personal Information, 2 Education, 3 Program of Interest (test
 * only), 4 Demographics, 5 Goals, 6 Review (submit), 7 class times after the
 * submit (control only), 8 submitted. Control skips index 3 and test skips
 * index 7, in both directions and on resume. What the applicant reads is the
 * display number: control "Step n of 4", test "Step n of 5".
 *
 * An application stores its variant (`form.programStepVariant`) when it is
 * created; that stored value wins over the live flag, and an application with
 * none is control.
 */

import type {ProgramStepVariant} from '@/lib/analytics'
import {classTimesFromSchedules} from './classTimes'

export type {ProgramStepVariant}

export const PROGRAM_STEP_FLAG = 'application-program-step-experiment'

/** How long a new application waits for the flag before it falls back. */
export const PROGRAM_STEP_FLAG_TIMEOUT_MS = 3000

export type ProgramStepVariantSource = 'flag' | 'fallback'

/** The variant one application form runs, and where it came from. */
export type ProgramStep = {
  variant: ProgramStepVariant
  source?: ProgramStepVariantSource
}

export const PROGRAM_STEP = 3
export const REVIEW_STEP = 6
export const SCHEDULE_STEP = 7
export const SUBMITTED_STEP = 8

const CONTENT_STEPS: Record<ProgramStepVariant, readonly number[]> = {
  control: [1, 2, 4, 5],
  test: [1, 2, 3, 4, 5],
}

const SKIPPED_STEPS: Record<ProgramStepVariant, readonly number[]> = {
  control: [PROGRAM_STEP],
  test: [SCHEDULE_STEP],
}

export const CONTROL_PROGRAM_STEP: ProgramStep = {variant: 'control'}

export function isProgramStepVariant(
  value: unknown,
): value is ProgramStepVariant {
  return value === 'control' || value === 'test'
}

function isProgramStepVariantSource(
  value: unknown,
): value is ProgramStepVariantSource {
  return value === 'flag' || value === 'fallback'
}

type ApplicationLike = {
  form?: {
    step?: number | null
    programStepVariant?: unknown
    programStepVariantSource?: unknown
  } | null
  // GET /campus-app's saved class times (test variant), when returned.
  preferredClassSchedule?: unknown
  optionalClassSchedule?: unknown
} | null

/** The variant an application stored when it was created, if any. */
export function storedProgramStep(
  app: ApplicationLike | undefined,
): ProgramStep | undefined {
  const variant = app?.form?.programStepVariant
  if (!isProgramStepVariant(variant)) return undefined
  const source = app?.form?.programStepVariantSource
  return isProgramStepVariantSource(source) ? {variant, source} : {variant}
}

/** An existing application's variant: its stored one, else control. */
export function programStepForApplication(
  app: ApplicationLike | undefined,
): ProgramStep {
  return storedProgramStep(app) ?? CONTROL_PROGRAM_STEP
}

/**
 * What the page remembers of a new application's assignment: the variant and,
 * once the create returns, the application's id.
 */
export type ProgramStepSession = ProgramStep & {appId?: string}

/**
 * The variant for the form the page has open (`appId`: an application id,
 * `'new'`, or nothing). A new application has none until its assignment
 * resolves. An existing one uses its stored variant; one created in this
 * session whose stored value has not come back uses the assignment it was
 * created with; any other is control.
 */
export function programStepForNewSession(
  appId: string | undefined,
  app: ApplicationLike | undefined,
  session: ProgramStepSession | undefined,
): ProgramStep | undefined {
  if (!appId) return undefined
  if (appId === 'new') {
    return session
      ? {variant: session.variant, source: session.source}
      : undefined
  }
  const stored = storedProgramStep(app)
  if (stored) return stored
  if (session?.appId === appId) {
    return {variant: session.variant, source: session.source}
  }
  return CONTROL_PROGRAM_STEP
}

/**
 * A new application's variant from the flag: the flag's own `control` or
 * `test`, or control tagged `fallback` once the flags have loaded without one
 * (flag missing, off for this person, or failed to load) or the wait timed
 * out. `undefined` while it is still worth waiting.
 */
export function programStepFromFlag(
  flagValue: unknown,
  {flagsLoaded = false, timedOut = false} = {},
): ProgramStep | undefined {
  if (isProgramStepVariant(flagValue)) {
    return {variant: flagValue, source: 'flag'}
  }
  if (flagsLoaded || timedOut) {
    return {variant: 'control', source: 'fallback'}
  }
  return undefined
}

function skips(step: number, variant: ProgramStepVariant): boolean {
  return SKIPPED_STEPS[variant].includes(step)
}

/** The step after `step`, skipping the variant's unused index. */
export function nextStep(step: number, variant: ProgramStepVariant): number {
  let next = step + 1
  while (skips(next, variant)) next += 1
  return next
}

/** The step before `step`, skipping the variant's unused index. */
export function previousStep(
  step: number,
  variant: ProgramStepVariant,
): number {
  let previous = step - 1
  while (previous > 1 && skips(previous, variant)) previous -= 1
  return Math.max(previous, 1)
}

/**
 * Where a form opens: step 1 for a new application, else the step after the
 * one the application last saved. An application saved at step 1 reopens at
 * step 1, as it always has. In the test variant, step 3 is saved before its
 * class times: an application saved there with no class times (the schedule
 * save failed, or the applicant left in between) reopens at step 3 to ask for
 * them.
 */
export function resumeStep(
  app: ApplicationLike | undefined,
  variant: ProgramStepVariant,
): number {
  if (!app) return 1
  const saved = app.form?.step
  if (saved === 1) return 1
  if (
    variant === 'test' &&
    saved === PROGRAM_STEP &&
    !classTimesFromSchedules(
      app.preferredClassSchedule,
      app.optionalClassSchedule,
    )
  ) {
    return PROGRAM_STEP
  }
  return nextStep(saved ?? 0, variant)
}

/** How many content steps the variant shows ("of 4" or "of 5"). */
export function displayStepCount(variant: ProgramStepVariant): number {
  return CONTENT_STEPS[variant].length
}

/**
 * The step number an applicant reads for a persisted index: control's
 * indices 1, 2, 4, 5 read 1-4 and test's 1-5 read 1-5. An index past the
 * content steps reads as the last one; control's unused index 3 (an
 * application saved before the removal) reads as 2.
 */
export function displayStepNumber(
  step: number,
  variant: ProgramStepVariant,
): number {
  return CONTENT_STEPS[variant].filter(index => index <= step).length
}

/** Progress through the content steps, 0-100, for a persisted index. */
export function stepProgressPercent(
  step: number,
  variant: ProgramStepVariant,
): number {
  return (displayStepNumber(step, variant) / displayStepCount(variant)) * 100
}
